export async function fetchAfterCurrent(query, isActive = () => true) {
  if (!query || !isActive()) return;
  if (query.state.isFetching) {
    // An initial fetch cannot be cancelled by invalidation in React Query v3.
    await query.fetch().catch(() => {});
  }
  if (isActive()) return query.fetch();
}

export function createQueryRefreshCoordinator({queryClient, isActive}) {
  const jobs = new Map();
  let disposed = false;
  const run = async (query, job) => {
    job.timer = null;
    if (disposed || job.running) return;
    if (!isActive()) {
      job.timer = setTimeout(() => run(query, job), 5000);
      return;
    }
    job.running = true;
    const canRefresh = () => !disposed && isActive() && query.isActive();
    try {
      while (!disposed && job.pending && isActive()) {
        job.pending = false;
        if (!query.isActive()) break;
        await fetchAfterCurrent(query, canRefresh);
        if (!isActive()) job.pending = true;
      }
      job.delay = 1000;
    } catch (error) {
      // 404 is terminal; retrying cannot make an unknown resource exist.
      if (error?.response?.status === 404) {
        job.pending = false;
        return;
      }
      job.pending = true;
      query.invalidate();
    } finally {
      job.running = false;
      if (!disposed && job.pending) {
        job.timer = setTimeout(() => run(query, job), job.delay);
        job.delay = Math.min(job.delay * 2, 30000);
      } else {
        jobs.delete(query);
      }
    }
  };
  return {
    invalidate(filters) {
      if (disposed) return;
      queryClient.getQueryCache().findAll(filters).forEach(query => {
        query.invalidate();
        if (!query.isActive()) return;
        let job = jobs.get(query);
        if (!job) {
          job = {pending: true, running: false, delay: 1000};
          jobs.set(query, job);
          run(query, job);
        } else {
          job.pending = true;
        }
      });
    },
    dispose() {
      disposed = true;
      jobs.forEach(job => clearTimeout(job.timer));
      jobs.clear();
    },
  };
}
